
/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useRouter } from 'src/routes/hooks';
import Iconify from 'src/components/iconify';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Fade,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Divider,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  Email,
  Phone,
  Videocam,
  Person,
  Business,
  Groups,
  ArrowForward,
  Shield,
  AccessTime,
  StarBorder,
} from '@mui/icons-material';
import axiosInstance from 'src/api/axiosInstance';

// ─── Brand tokens (derived from MeruDesk logo) ───────────────────────────────
const BRAND = {
  teal: '#00B5A3',
  tealLight: '#E6F7F6',
  tealMid: '#00C9B1',
  tealDark: '#008F82',
  green: '#3CB371',
  greenLight: '#EAF6EE',
  navy: '#1A2B4A',
  navyLight: '#2D4270',
  bodyText: '#374151',
  mutedText: '#6B7280',
  borderColor: '#E5E7EB',
  bgSurface: '#F9FAFB',
  white: '#FFFFFF',
  errorRed: '#EF4444',
  successGreen: '#10B981',
};

// ─── PhoneCodeOption ──────────────────────────────────────────────────────────
const PhoneCodeOption = ({ option, optionProps }) => {
  const label = `+${option.phoneCode} (${option.countryName || option.shortName || ''})`;
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const el = textRef.current;
      if (!el) return;
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    };
    checkOverflow();
    const el = textRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [label]);

  return (
    <Tooltip
      title={label}
      placement="right"
      arrow
      disableHoverListener={!isOverflowing}
      disableFocusListener={!isOverflowing}
      disableTouchListener={!isOverflowing}
    >
      <Box
        component="li"
        {...optionProps}
        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        <Box
          ref={textRef}
          component="span"
          sx={{
            display: 'block',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Box>
      </Box>
    </Tooltip>
  );
};

PhoneCodeOption.propTypes = {
  option: PropTypes.shape({
    phoneCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    countryName: PropTypes.string,
    shortName: PropTypes.string,
  }).isRequired,
  optionProps: PropTypes.object.isRequired,
};

// ─── Trust badges shown below the CTA button ─────────────────────────────────
const TRUST_ITEMS = [
  { icon: <Shield sx={{ fontSize: 13 }} />, label: 'No spam, ever' },
  { icon: <AccessTime sx={{ fontSize: 13 }} />, label: '30-min session' },
  { icon: <StarBorder sx={{ fontSize: 13 }} />, label: 'No credit card' },
];

// ─── Main Page ────────────────────────────────────────────────────────────────
const BookDemoPage = () => {
  const navigate = useNavigate();
  const router = useRouter();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneCode: '',
    phone: '',
    company: '',
    companySize: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [phoneCodes, setPhoneCodes] = useState([]);

  // ── Fetch country dial codes ──────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;
    const fetchCountries = async () => {
      try {
        const response = await axiosInstance.get('/rest/global/countries/getallcontries');
        if (isMounted) {
          const fetched = response.data || [];
          const unique = Array.from(
            new Map(fetched.map((c) => [c.phoneCode, c])).values()
          ).filter((c) => c.phoneCode);
          setPhoneCodes(unique);
          const india = unique.find((c) => String(c.phoneCode) === '91');
          if (india) setFormData((p) => ({ ...p, phoneCode: india.countriesId }));
        }
      } catch (err) {
        console.error('Error fetching countries:', err);
      }
    };
    fetchCountries();
    return () => { isMounted = false; };
  }, []);

  // ── Field change handler ──────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    let filtered = value;
    if (name === 'phone') {
      filtered = value.replace(/\D/g, '');
      if (filtered.length > 15) return;
    }
    setFormData((p) => ({ ...p, [name]: filtered }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: null }));
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validateForm = () => {
    const e = {};
    if (!formData.firstName.trim()) e.firstName = 'First name is required';
    if (!formData.lastName.trim()) e.lastName = 'Last name is required';
    if (!formData.email.trim()) e.email = 'Work email is required';
    else if (!/^\S+@\S+\.\S+$/.test(formData.email)) e.email = 'Enter a valid email address';
    if (!formData.phoneCode) e.phoneCode = 'Select a country code';
    if (!formData.phone) e.phone = 'Phone number is required';
    else if (formData.phone.length < 7) e.phone = 'Enter a valid phone number';
    if (!formData.company.trim()) e.company = 'Company name is required';
    if (!formData.companySize) e.companySize = 'Please select a company size';
    if (Object.keys(e).length > 0) { setErrors(e); return false; }
    return true;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      mobPhoneCode: formData.phoneCode ? { countriesId: formData.phoneCode } : null,
      company: formData.company,
      companySize: formData.companySize,
      message: formData.message,
    };
    try {
      const response = await axiosInstance.post('/rest/demo-request', payload);
      console.log('Demo request submitted:', response.data);
      setIsSuccess(true);
    } catch (err) {
      console.error('Submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavigateHome = () => router.push('/');

  // ── Shared input height ───────────────────────────────────────────────────
  const INPUT_H = 44;

  // ── Base text-field style ─────────────────────────────────────────────────
  const fieldSx = {
    mb: '18px',
    position: 'relative',
    '& .MuiInputLabel-root': {
      fontSize: '0.8rem',
      color: BRAND.mutedText,
      '&.Mui-focused': { color: BRAND.teal },
    },
    '& .MuiOutlinedInput-root': {
      bgcolor: BRAND.white,
      borderRadius: '10px',
      height: INPUT_H,
      fontSize: '0.85rem',
      color: BRAND.navy,
      '& fieldset': { borderColor: BRAND.borderColor, borderWidth: '1.5px' },
      '&:hover fieldset': { borderColor: BRAND.teal },
      '&.Mui-focused fieldset': { borderColor: BRAND.teal, borderWidth: '2px' },
      '&.Mui-error fieldset': { borderColor: BRAND.errorRed },
    },
    '& .MuiInputAdornment-root': { height: INPUT_H, maxHeight: INPUT_H, alignSelf: 'center' },
    '& .MuiInputBase-input': {
      fontSize: '0.85rem',
      py: 0,
      lineHeight: `${INPUT_H}px`,
      height: `${INPUT_H}px`,
      boxSizing: 'border-box',
      color: BRAND.navy,
      '&::placeholder': { color: BRAND.mutedText, opacity: 0.7 },
    },
    '& .MuiFormHelperText-root': {
      position: 'absolute',
      bottom: '-16px',
      left: 0,
      margin: 0,
      fontSize: '0.68rem',
      lineHeight: 1.2,
      color: BRAND.errorRed,
    },
  };

  const textareaSx = {
    mb: '14px',
    '& .MuiInputLabel-root': {
      fontSize: '0.8rem',
      color: BRAND.mutedText,
      '&.Mui-focused': { color: BRAND.teal },
    },  
    '& .MuiOutlinedInput-root': {
      bgcolor: BRAND.white,
      borderRadius: '10px',
      fontSize: '0.85rem',
      color: BRAND.navy,
      '& fieldset': { borderColor: BRAND.borderColor, borderWidth: '1.5px' },
      '&:hover fieldset': { borderColor: BRAND.teal },
      '&.Mui-focused fieldset': { borderColor: BRAND.teal, borderWidth: '2px' },
    },
    '& .MuiInputBase-input': {
      fontSize: '0.85rem',
      py: 1,
      px: 0.5,
      color: BRAND.navy,
      '&::placeholder': { color: BRAND.mutedText, opacity: 0.7 },
    },
  };

  const selectSx = {
    ...fieldSx,
    '& .MuiSelect-select': {
      fontSize: '0.85rem',
      display: 'flex',
      alignItems: 'center',
      height: `${INPUT_H}px !important`,
      py: '0 !important',
      boxSizing: 'border-box',
      color: BRAND.navy,
    },
  };

  // ── Icon colour for adornments ────────────────────────────────────────────
  const adornColor = BRAND.tealDark;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: BRAND.white,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* ── Subtle teal wash top-left ── */}
      <Box
        sx={{
          position: 'absolute',
          top: -160,
          left: -120,
          width: 520,
          height: 520,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${BRAND.tealLight} 0%, transparent 70%)`,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      {/* ── Subtle green wash bottom-right ── */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -140,
          right: -100,
          width: 460,
          height: 460,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${BRAND.greenLight} 0%, transparent 70%)`,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* ══════════════════════════════════════════
          TOP NAVIGATION BAR
      ══════════════════════════════════════════ */}
      <Box
        sx={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2.5, md: 5 },
          borderBottom: `1px solid ${BRAND.borderColor}`,
          bgcolor: BRAND.white,
          flexShrink: 0,
          zIndex: 10,
          boxShadow: '0 1px 0 0 rgba(0,0,0,0.04)',
        }}
      >
        {/* Logo */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ cursor: 'pointer' }}
          onClick={handleNavigateHome}
        >
          {/* Mountain icon — matches logo mark */}

          <Box
            component="img"
            src="/assets/MeruDesk.png"
            alt="MeruDesk"
            sx={{ height: 28, width: 'auto', display: 'block' }}
            onError={(e) => {
              // Fallback wordmark if image doesn't load
              e.currentTarget.style.display = 'none';
            }}
          />

        </Stack>

        {/* Back to Home */}
        <Button
          onClick={handleNavigateHome}
          startIcon={<Iconify icon="solar:home-2-bold-duotone" width={18} />}
          variant="contained"
          size="small"
          sx={{
            fontWeight: 700,
            px: 1.5,
            py: 0.6,
            borderRadius: 1,
            bgcolor: 'primary.main',
            color: 'common.white',
            boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`,
            transition: 'all 0.3s',
            '&:hover': {
              bgcolor: 'primary.dark',
              transform: 'translateY(-1px)',
              boxShadow: (theme) => `0 8px 16px ${theme.palette.primary.main}44`,
            },
          }}
        >
          Back to Home
        </Button>
      </Box>

      {/* ══════════════════════════════════════════
          MAIN CONTENT
      ══════════════════════════════════════════ */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          px: { xs: 2, md: 3 },
          py: { xs: 2.5, md: 3.5 },
          overflowY: 'auto',
          zIndex: 5,
        }}
      >
        <Container maxWidth="sm" disableGutters>
          <Fade in timeout={500}>
            <Box>

              {/* ── Page headline (above card) ── */}
              <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                {/* Eyebrow pill */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '20px',
                    bgcolor: BRAND.tealLight,
                    border: `1px solid ${BRAND.teal}30`,
                    mb: 1.25,
                  }}
                >
                  <Videocam sx={{ fontSize: 13, color: BRAND.teal }} />
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      color: BRAND.tealDark,
                      letterSpacing: '0.9px',
                    }}
                  >
                    Free Expert Consultation
                  </Typography>
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: BRAND.navy,
                    letterSpacing: '-0.6px',
                    lineHeight: 1.15,
                    mb: 0.75,
                    fontSize: { xs: '1.6rem', md: '1.9rem' },
                  }}
                >
                  Transform Your Workforce with{' '}
                  <Box
                    component="span"
                    sx={{
                      background: `linear-gradient(90deg, ${BRAND.teal}, ${BRAND.green})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    MeruDesk
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: BRAND.mutedText,
                    maxWidth: 440,
                    mx: 'auto',
                    lineHeight: 1.5,
                  }}
                >
                  Fill in your details and we'll set up a personalised walkthrough.
                </Typography>
              </Box>

              {/* ══════════════════════════════════════
                  FORM CARD
              ══════════════════════════════════════ */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '18px',
                  width: '100%',
                  border: `1.5px solid ${BRAND.borderColor}`,
                  bgcolor: BRAND.white,
                  boxShadow: '0 8px 40px -8px rgba(0,181,163,0.10), 0 2px 12px -2px rgba(26,43,74,0.07)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Top teal accent stripe */}
                <Box
                  sx={{
                    height: 4,
                    background: `linear-gradient(90deg, ${BRAND.teal}, ${BRAND.green})`,
                    borderRadius: '18px 18px 0 0',
                  }}
                />

                {/* ── Success overlay ── */}
                <Fade in={isSuccess}>
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: BRAND.white,
                      zIndex: 50,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 4,
                      py: 6,
                      textAlign: 'center',
                    }}
                  >
                    {/* Animated check ring */}
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${BRAND.tealLight}, ${BRAND.greenLight})`,
                        border: `2px solid ${BRAND.teal}40`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          inset: -6,
                          borderRadius: '50%',
                          border: `1.5px solid ${BRAND.teal}20`,
                        },
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 38, color: BRAND.successGreen }} />
                    </Box>

                    <Chip
                      label="Request received"
                      size="small"
                      sx={{
                        bgcolor: BRAND.tealLight,
                        color: BRAND.tealDark,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        mb: 2,
                        border: `1px solid ${BRAND.teal}30`,
                      }}
                    />

                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: BRAND.navy,
                        letterSpacing: '-0.4px',
                        mb: 1,
                      }}
                    >
                      You're all set!
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: BRAND.mutedText,
                        maxWidth: 320,
                        mx: 'auto',
                        mb: 3,
                        lineHeight: 1.7,
                      }}
                    >
                      Our team will review your details and reach out within{' '}
                      <Box component="span" sx={{ fontWeight: 700, color: BRAND.teal }}>
                        1 business day
                      </Box>{' '}
                      with available time slots.
                    </Typography>

                    {/* Timeline steps */}
                    {[
                      { step: '1', text: 'Confirmation email on its way to your inbox' },
                      { step: '2', text: 'Our team reviews your requirements' },
                      { step: '3', text: 'We schedule your personalised demo' },
                    ].map((item) => (
                      <Box
                        key={item.step}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          mb: 1.25,
                          px: 2,
                          py: 1,
                          borderRadius: '10px',
                          bgcolor: BRAND.bgSurface,
                          width: '100%',
                          maxWidth: 340,
                          border: `1px solid ${BRAND.borderColor}`,
                        }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${BRAND.teal}, ${BRAND.green})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: '#fff' }}>
                            {item.step}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.78rem', color: BRAND.bodyText }}>
                          {item.text}
                        </Typography>
                      </Box>
                    ))}

                    <Button
                      onClick={handleNavigateHome}
                      endIcon={<ArrowForward sx={{ fontSize: 15 }} />}
                      sx={{
                        mt: 3,
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        color: BRAND.teal,
                        '&:hover': { color: BRAND.tealDark, bgcolor: BRAND.tealLight },
                      }}
                    >
                      Back to MeruDesk
                    </Button>
                  </Box>
                </Fade>

                {/* ── Form body ── */}
                <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
                  <form onSubmit={handleSubmit} noValidate>

                    {/* Section: Personal details */}
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        color: BRAND.teal,

                        letterSpacing: '1px',
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&::after': {
                          content: '""',
                          flex: 1,
                          height: '1px',
                          bgcolor: BRAND.tealLight,
                          borderRadius: 1,
                        },
                      }}
                    >
                      Your details
                    </Typography>

                    {/* Row 1: First + Last name */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 0 }}>
                      <TextField
                        name="firstName"
                        label={<span>First Name <span style={{ color: BRAND.errorRed }}>*</span></span>}
                        value={formData.firstName}
                        onChange={handleChange}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        disabled={isSubmitting}
                        fullWidth
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person sx={{ fontSize: 16, color: adornColor }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        name="lastName"
                        label={<span>Last Name <span style={{ color: BRAND.errorRed }}>*</span></span>}
                        value={formData.lastName}
                        onChange={handleChange}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                        disabled={isSubmitting}
                        fullWidth
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person sx={{ fontSize: 16, color: adornColor }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>

                    {/* Row 2: Work email */}
                    <TextField
                      name="email"
                      label={<span>Work Email <span style={{ color: BRAND.errorRed }}>*</span></span>}
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      disabled={isSubmitting}
                      fullWidth
                      sx={fieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email sx={{ fontSize: 16, color: adornColor }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* Row 3: Phone with country code */}
                    <TextField
                      name="phone"
                      label={<span>Phone Number <span style={{ color: BRAND.errorRed }}>*</span></span>}
                      value={formData.phone}
                      onChange={handleChange}
                      error={!!errors.phone || !!errors.phoneCode}
                      helperText={[errors.phoneCode, errors.phone].filter(Boolean).join(' · ')}
                      disabled={isSubmitting}
                      fullWidth
                      sx={fieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Autocomplete
                              options={phoneCodes}
                              getOptionLabel={(opt) => `+${opt.phoneCode || ''}`}
                              isOptionEqualToValue={(opt, val) => opt.countriesId === val.countriesId}
                              value={phoneCodes.find((c) => c.countriesId === formData.phoneCode) || null}
                              filterOptions={(options, state) => {
                                const q = state.inputValue.toLowerCase();
                                return options.filter(
                                  (o) =>
                                    String(o.phoneCode || '').toLowerCase().includes(q) ||
                                    String(o.shortName || '').toLowerCase().includes(q) ||
                                    String(o.countryName || '').toLowerCase().includes(q)
                                );
                              }}
                              renderOption={(props, option) => (
                                <PhoneCodeOption option={option} optionProps={props} />
                              )}
                              componentsProps={{ paper: { sx: { minWidth: 220 } } }}
                              onChange={(_, newVal) => {
                                setFormData((p) => ({ ...p, phoneCode: newVal ? newVal.countriesId : '' }));
                                if (errors.phoneCode) setErrors((p) => ({ ...p, phoneCode: null }));
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="standard"
                                  placeholder="+Code"
                                  InputProps={{
                                    ...params.InputProps,
                                    disableUnderline: true,
                                    style: { fontSize: '0.85rem', fontWeight: 600, padding: 0 },
                                  }}
                                  sx={{
                                    width: 105,
                                    '& .MuiInputBase-root': {
                                      pr: '22px !important',
                                    },
                                    '& .MuiInputBase-input': {
                                      textAlign: 'center',
                                      pl: 0,
                                      color: BRAND.navy,
                                    },
                                    '& .MuiAutocomplete-endAdornment': {
                                      right: 0,
                                    },
                                  }}
                                />
                              )}
                              disableClearable
                            />
                            <Divider orientation="vertical" flexItem sx={{ ml: 0.75, my: 1.25 }} />
                            <Phone sx={{ fontSize: 16, color: adornColor, ml: 0.75 }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* Divider before company section */}
                    <Box sx={{ mb: 2 }} />
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        color: BRAND.teal,

                        letterSpacing: '1px',
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&::after': {
                          content: '""',
                          flex: 1,
                          height: '1px',
                          bgcolor: BRAND.tealLight,
                          borderRadius: 1,
                        },
                      }}
                    >
                      Company details
                    </Typography>

                    {/* Row 4: Company name + size */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 0 }}>
                      <TextField
                        name="company"
                        label={<span>Company Name <span style={{ color: BRAND.errorRed }}>*</span></span>}
                        value={formData.company}
                        onChange={handleChange}
                        error={!!errors.company}
                        helperText={errors.company}
                        disabled={isSubmitting}
                        fullWidth
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Business sx={{ fontSize: 16, color: adornColor }} />
                            </InputAdornment>
                          ),
                        }}
                      />

                      {/* Company size select */}
                      <TextField
                        select
                        name="companySize"
                        {...(formData.companySize
                          ? {
                            label: (
                              <span>
                                Team Size <span style={{ color: BRAND.errorRed }}>*</span>
                              </span>
                            ),
                            InputLabelProps: { shrink: true, sx: { fontSize: '0.8rem' } },
                          }
                          : {})}
                        value={formData.companySize}
                        onChange={handleChange}
                        error={!!errors.companySize}
                        helperText={errors.companySize}
                        disabled={isSubmitting}
                        fullWidth
                        sx={selectSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Groups sx={{ fontSize: 16, color: adornColor }} />
                            </InputAdornment>
                          ),
                        }}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (selected) => {
                            if (!selected)
                              return (
                                <Typography sx={{ fontSize: '0.8rem', color: BRAND.mutedText, opacity: 0.8 }}>
                                  Team Size *
                                </Typography>
                              );
                            const map = {
                              '1-50': '1–50 employees',
                              '51-200': '51–200 employees',
                              '201-500': '201–500 employees',
                              '501+': '500+ employees',
                            };
                            return (
                              <Typography sx={{ fontSize: '0.85rem', color: BRAND.navy }}>
                                {map[selected] || selected}
                              </Typography>
                            );
                          },
                        }}
                      >
                        <MenuItem value="1-50">1–50 employees</MenuItem>
                        <MenuItem value="51-200">51–200 employees</MenuItem>
                        <MenuItem value="201-500">201–500 employees</MenuItem>
                        <MenuItem value="501+">500+ employees</MenuItem>
                      </TextField>
                    </Stack>

                    {/* Row 5: Message (optional) */}
                    <TextField
                      name="message"
                      label="What would you like to see in the demo? (optional)"
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={2}
                      value={formData.message}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      fullWidth
                      placeholder="e.g. ticket routing, SLA reporting, team inbox setup…"
                      sx={textareaSx}
                    />

                    {/* ── Submit button ── */}
                    <Box
                      sx={{
                        pt: 1.5,
                        borderTop: `1px solid ${BRAND.borderColor}`,
                        mt: 1,
                      }}
                    >
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={isSubmitting}
                        endIcon={
                          !isSubmitting && (
                            <ArrowForward sx={{ fontSize: '16px !important' }} />
                          )
                        }
                        sx={{
                          height: 48,
                          borderRadius: '10px',
                          fontWeight: 800,
                          fontSize: '0.9rem',
                          letterSpacing: '0.2px',
                          background: `linear-gradient(90deg, ${BRAND.teal} 0%, ${BRAND.green} 100%)`,
                          boxShadow: `0 4px 16px ${BRAND.teal}35`,
                          transition: 'all 0.25s',
                          '&:hover': {
                            background: `linear-gradient(90deg, ${BRAND.tealDark} 0%, ${BRAND.teal} 100%)`,
                            boxShadow: `0 8px 24px ${BRAND.teal}45`,
                            transform: 'translateY(-1px)',
                          },
                          '&:active': { transform: 'translateY(0)' },
                          '&.Mui-disabled': {
                            background: `${BRAND.teal}55`,
                            color: 'white',
                          },
                        }}
                      >
                        {isSubmitting ? (
                          <CircularProgress size={18} sx={{ color: 'white' }} />
                        ) : (
                          'Book My Demo'
                        )}
                      </Button>

                      {/* Trust badges */}
                      <Stack
                        direction="row"
                        justifyContent="center"
                        spacing={2.5}
                        sx={{ mt: 1.75 }}
                      >
                        {TRUST_ITEMS.map((item) => (
                          <Box
                            key={item.label}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: BRAND.mutedText,
                            }}
                          >
                            <Box sx={{ color: BRAND.teal, lineHeight: 0 }}>{item.icon}</Box>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                              {item.label}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </form>
                </Box>
              </Paper>

              {/* Footer */}
              <Typography
                sx={{
                  textAlign: 'center',
                  mt: 3,
                  fontSize: '0.7rem',
                  color: BRAND.mutedText,
                  opacity: 0.65,
                }}
              >
                © {new Date().getFullYear()} CyberMeru Technologies Pvt. Ltd. · All rights reserved.
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default BookDemoPage;